import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { auth, db } from "../../firebase";
import { brandColors } from "../../theme";
import { alpha } from "@mui/material/styles";
import { T } from "../Translate";
import PaypalButton from "../../components/PaypalButton";
import { STORE_COMMISSION_RATE, getPendingCommission, markCommissionPaid } from "./storesUtils";

const formatMoney = (value) =>
  new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(Number(value) || 0);

const formatDateShort = (date) =>
  date.toLocaleDateString(undefined, { day: "numeric", month: "short" });

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Serie diaria de ingresos, últimos `days` días (incluye hoy, con ceros en
// los días sin ventas para que el eje no se salte fechas).
const buildDailySeries = (sales, days) => {
  const buckets = new Map();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    buckets.set(d.toDateString(), { date: d, total: 0 });
  }

  sales.forEach((sale) => {
    const d = toDate(sale.purchaseDate);
    if (!d) return;
    const key = new Date(d.getFullYear(), d.getMonth(), d.getDate()).toDateString();
    const bucket = buckets.get(key);
    if (bucket) bucket.total += Number(sale.totalAmount) || 0;
  });

  return Array.from(buckets.values());
};

// Barra simple de un solo color (ingresos = magnitud, una sola serie: no
// hace falta leyenda, el título del bloque ya dice qué se está mostrando).
const RevenueBarChart = ({ series }) => {
  const [hovered, setHovered] = useState(null);
  const width = 100; // porcentaje, viewBox escalable
  const height = 160;
  const barGap = 2;
  const max = Math.max(1, ...series.map((s) => s.total));
  const barSlot = width / series.length;
  const barWidth = Math.max(2, barSlot - barGap);
  const baselineY = height - 24;
  const plotHeight = baselineY - 10;

  const ticks = [0, max / 2, max];

  return (
    <Box sx={{ position: "relative" }}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={200} preserveAspectRatio="none" role="img" aria-label="Ingresos por día">
        {ticks.map((t, i) => {
          const y = baselineY - (t / max) * plotHeight;
          return (
            <line
              key={i}
              x1={0}
              x2={width}
              y1={y}
              y2={y}
              stroke={brandColors.mode === 'light' ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.12)'}
              strokeWidth={0.3}
            />
          );
        })}
        {series.map((s, i) => {
          const barHeight = (s.total / max) * plotHeight;
          const x = i * barSlot + barGap / 2;
          const y = baselineY - barHeight;
          const isHovered = hovered === i;
          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={Math.max(barHeight, s.total > 0 ? 1 : 0)}
                rx={1}
                fill={isHovered ? brandColors.accent : alpha(brandColors.accent, 0.55)}
                style={{ cursor: s.total > 0 ? "pointer" : "default", transition: "fill 0.15s ease" }}
              />
              {/* hit target más grande que la barra, cubre todo el carril */}
              <rect
                x={i * barSlot}
                y={10}
                width={barSlot}
                height={plotHeight}
                fill="transparent"
                onPointerEnter={() => setHovered(i)}
                onPointerLeave={() => setHovered((h) => (h === i ? null : h))}
              />
            </g>
          );
        })}
      </svg>
      {hovered != null && (
        <Box
          sx={{
            position: "absolute",
            top: 4,
            left: 4,
            bgcolor: brandColors.backgroundDefault,
            border: `1px solid ${brandColors.divider}`,
            borderRadius: 1,
            px: 1.25,
            py: 0.75,
            pointerEvents: "none",
          }}
        >
          <Typography variant="caption" sx={{ color: brandColors.textSecondary, display: "block" }}>
            {formatDateShort(series[hovered].date)}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: brandColors.textPrimary }}>
            {formatMoney(series[hovered].total)}
          </Typography>
        </Box>
      )}
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {formatDateShort(series[0].date)}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {formatDateShort(series[series.length - 1].date)}
        </Typography>
      </Stack>
    </Box>
  );
};

const StatCard = ({ label, value, hint }) => (
  <Paper
    variant="outlined"
    sx={{ p: { xs: 1.75, sm: 2.5 }, borderRadius: 3, backgroundColor: brandColors.backgroundPaper, borderColor: "divider", height: "100%", minWidth: 0 }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", overflowWrap: "break-word" }}>
      {label}
    </Typography>
    <Typography
      variant="h4"
      sx={{
        fontWeight: 700,
        mt: 0.5,
        fontSize: { xs: "1.25rem", sm: "1.75rem", md: "2.125rem" },
        overflowWrap: "break-word",
      }}
    >
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" sx={{ color: "text.secondary", overflowWrap: "break-word" }}>
        {hint}
      </Typography>
    )}
  </Paper>
);

// Contenido del panel de control, para usar embebido como pestaña dentro de
// StoreDetail.js (el chequeo de dueño y la carga de la tienda ya los hace
// ese componente padre — acá solo se traen las ventas).
const StoreDashboardTab = ({ storeSlug, storeData, onCommissionPaid }) => {
  const [loadingSales, setLoadingSales] = useState(true);
  const [sales, setSales] = useState([]);
  const [rangeDays, setRangeDays] = useState(14);
  const [pendingCommission, setPendingCommission] = useState({ amount: 0, salesCount: 0 });
  const [commissionFeedback, setCommissionFeedback] = useState(null);

  const refreshPendingCommission = React.useCallback(() => {
    const user = auth.currentUser;
    if (!user) return;
    getPendingCommission(storeSlug, user.uid).then(setPendingCommission);
  }, [storeSlug]);

  useEffect(() => {
    refreshPendingCommission();
  }, [refreshPendingCommission, sales]);

  useEffect(() => {
    let active = true;
    const user = auth.currentUser;
    if (!user) return undefined;

    setLoadingSales(true);
    db.collection("sales")
      .where("storeId", "==", storeSlug)
      .where("sellerId", "==", user.uid)
      .get()
      .then((snap) => {
        if (!active) return;
        setSales(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingSales(false);
      })
      .catch((error) => {
        console.error("Error al cargar las ventas:", error);
        if (active) setLoadingSales(false);
      });

    return () => {
      active = false;
    };
  }, [storeSlug]);

  const stats = useMemo(() => {
    const totalRevenue = sales.reduce((acc, s) => acc + (Number(s.totalAmount) || 0), 0);
    const totalOrders = sales.length;
    const avgOrder = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    return { totalRevenue, totalOrders, avgOrder };
  }, [sales]);

  const dailySeries = useMemo(() => buildDailySeries(sales, rangeDays), [sales, rangeDays]);

  const topProducts = useMemo(() => {
    const byProduct = new Map();
    sales.forEach((s) => {
      const key = s.productId || s.productName;
      if (!key) return;
      const entry = byProduct.get(key) || { name: s.productName, revenue: 0, count: 0 };
      entry.revenue += Number(s.totalAmount) || 0;
      entry.count += 1;
      byProduct.set(key, entry);
    });
    return Array.from(byProduct.values())
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  }, [sales]);

  const recentSales = useMemo(() => {
    return [...sales]
      .sort((a, b) => {
        const dateA = toDate(a.purchaseDate)?.getTime() || 0;
        const dateB = toDate(b.purchaseDate)?.getTime() || 0;
        return dateB - dateA;
      })
      .slice(0, 10);
  }, [sales]);

  if (loadingSales) {
    return (
      <Typography variant="body2" sx={{ color: "text.secondary", py: 4, textAlign: "center" }}>
        <T>Cargando estadísticas...</T>
      </Typography>
    );
  }

  return (
    <Stack spacing={3}>
      {!storeData.paypalEmail && (
        <Alert severity="warning">
          <T>Todavía no configuraste un correo de PayPal — los clientes no van a poder pagarte hasta que lo cargues en "Apariencia y datos".</T>
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <StatCard label={<T>Ventas totales</T>} value={formatMoney(stats.totalRevenue)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label={<T>Pedidos</T>} value={stats.totalOrders} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label={<T>Ticket promedio</T>} value={formatMoney(stats.avgOrder)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label={<T>Comisión pendiente</T>}
            value={formatMoney(pendingCommission.amount)}
            hint={<T>{STORE_COMMISSION_RATE * 100}% de {pendingCommission.salesCount} venta(s) sin pagar</T>}
          />
        </Grid>
      </Grid>

      {pendingCommission.amount > 0 && (
        <Paper
          variant="outlined"
          sx={{ p: 2.5, borderRadius: 3, backgroundColor: brandColors.backgroundPaper, borderColor: "divider" }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                <T>Pagar comisión pendiente</T>
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                <T>Notiline cobra {STORE_COMMISSION_RATE * 100}% de referencia por cada venta. Mientras haya comisión pendiente, no vas a poder cargar productos nuevos.</T>
              </Typography>
            </Box>
            {commissionFeedback && (
              <Alert severity={commissionFeedback.type} onClose={() => setCommissionFeedback(null)}>
                {commissionFeedback.message}
              </Alert>
            )}
            <PaypalButton
              amount={pendingCommission.amount}
              description={`Comisión Notiline - ${storeData.storeName}`}
              clientId={process.env.REACT_APP_PAYPAL_CLIENT_ID}
              payeeEmail={process.env.REACT_APP_PLATFORM_PAYPAL_EMAIL}
              metadata={{ customId: storeSlug }}
              onApprove={(details) => {
                const user = auth.currentUser;
                markCommissionPaid({
                  storeSlug,
                  sellerId: user.uid,
                  amount: pendingCommission.amount,
                  paypalTransactionId: details.id,
                })
                  .then(() => {
                    setCommissionFeedback({ type: "success", message: <T>Comisión pagada. ¡Gracias!</T> });
                    refreshPendingCommission();
                    onCommissionPaid?.();
                  })
                  .catch((error) => {
                    console.error("Error al registrar el pago de comisión:", error);
                    setCommissionFeedback({ type: "warning", message: <T>El pago se realizó pero no pudimos registrarlo. Contactanos.</T> });
                  });
              }}
              onError={() => setCommissionFeedback({ type: "error", message: <T>Ocurrió un error al procesar el pago.</T> })}
            />
          </Stack>
        </Paper>
      )}

      <Paper
        variant="outlined"
        sx={{ p: 2.5, borderRadius: 3, backgroundColor: brandColors.backgroundPaper, borderColor: "divider" }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            <T>Ingresos por día</T>
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={rangeDays}
            onChange={(e, value) => value && setRangeDays(value)}
          >
            <ToggleButton value={7}>7d</ToggleButton>
            <ToggleButton value={14}>14d</ToggleButton>
            <ToggleButton value={30}>30d</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {stats.totalOrders === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary", py: 4, textAlign: "center" }}>
            <T>Todavía no hay ventas para graficar.</T>
          </Typography>
        ) : (
          <RevenueBarChart series={dailySeries} />
        )}
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: 2.5, borderRadius: 3, backgroundColor: brandColors.backgroundPaper, borderColor: "divider" }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          <T>Productos más vendidos</T>
        </Typography>
        {topProducts.length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            <T>Todavía no hay ventas.</T>
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {topProducts.map((p, i) => (
              <Box key={i}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {p.name}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatMoney(p.revenue)}
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {p.count} <T>venta(s)</T>
                </Typography>
                {i < topProducts.length - 1 && <Divider sx={{ mt: 1.5 }} />}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: 2.5, borderRadius: 3, backgroundColor: brandColors.backgroundPaper, borderColor: "divider", overflowX: "auto" }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          <T>Últimas ventas</T>
        </Typography>
        {recentSales.length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            <T>Todavía no hay ventas.</T>
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><T>Fecha</T></TableCell>
                <TableCell><T>Producto</T></TableCell>
                <TableCell align="right"><T>Monto</T></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentSales.map((s) => {
                const d = toDate(s.purchaseDate);
                return (
                  <TableRow key={s.id}>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {d ? d.toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell>{s.productName}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatMoney(s.totalAmount)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
};

export default StoreDashboardTab;
